import { useMemo, useState } from "react";
import {
  IResourceComponentsProps,
  useApiUrl,
  useNavigation,
  useNotification,
  usePermissions,
  useShow,
} from "@pankod/refine-core";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Icons,
  Input,
  Modal,
  Popover,
  Progress,
  Radio,
  Row,
  Show,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "@pankod/refine-antd";

import { ConsumerLogs, WithoutFamilyPlan } from "interfaces";
import {
  OUTCOME,
  PROCESS_RUNNING_LABEL,
  SHELF_STATE,
  TONE_COLOR,
  UserRole,
  originLabel,
  processStatus,
} from "enums";
import { dateFormater } from "utils/date-formater";
import {
  formatDuration,
  formatElapsed,
  formatEstimate,
  formatSeconds,
} from "utils/duration";
import {
  BranchOfficeLabels,
  branchOfficeLabelText,
  branchOfficeSearchText,
  useBranchOfficeLabels,
} from "hooks/useBranchOfficeLabels";
import { axiosInstance } from "services/axios";
import {
  BranchRow,
  RamoSummary,
  RowFilter,
  WithoutFamilyGroup,
  WithoutFamilySummary,
  aggregateBranches,
  aggregateWithoutFamily,
  branchRows,
  branchWithoutFamilyCount,
  estimateRemaining,
  isEmpty,
  isFailure,
  metricNumber,
  processTone,
  summarize,
  withoutFamilyDisplay,
} from "./aggregations";

const { Text } = Typography;

// Reexportados: a suíte importa daqui desde a spec 035 e a fiação continua sendo a mesma.
export {
  aggregateWithoutFamily,
  withoutFamilyDisplay,
} from "./aggregations";
export type {
  WithoutFamilyGroup,
  WithoutFamilySummary,
} from "./aggregations";

const REFRESH_INTERVAL_MS = 5000;

// O recurso v1/plan-family é filho de "settings", então o Refine monta a rota
// como /settings/plans-family — sem o prefixo o link cai no catchAll (404).
const PLANS_FAMILY_ROUTE = "/settings/plans-family";

export const PlanReloadShow: React.FC<IResourceComponentsProps> = () => {
  const [rowFilter, setRowFilter] = useState<RowFilter>("all");
  const [search, setSearch] = useState("");

  const permissions = usePermissions();
  const isAdmin = permissions.data === UserRole.Admin;
  const apiUrl = useApiUrl();
  const toast = useNotification();
  const { list } = useNavigation();
  const branchLabels = useBranchOfficeLabels();
  const [redispatching, setRedispatching] = useState(false);

  const { queryResult } = useShow<ConsumerLogs>({
    queryOptions: {
      // Progresso ao vivo enquanto processa; encerra o polling ao finalizar (RN08 da 027).
      refetchInterval: (data) =>
        data?.data?.last_status !== "finalizado" ? REFRESH_INTERVAL_MS : false,
    },
  });
  const { data, isLoading, refetch, dataUpdatedAt } = queryResult;
  const record = data?.data;

  const withoutFamily = useMemo(
    () => aggregateWithoutFamily(record?.statistics),
    [record]
  );
  const rows = useMemo(() => branchRows(record), [record]);
  const summary = useMemo(
    () => summarize(rows, record?.last_status, withoutFamily),
    [rows, record, withoutFamily]
  );
  const ramos = useMemo(() => aggregateBranches(rows), [rows]);

  const tone = processTone(summary);
  const status = summary.done
    ? processStatus(record?.last_status, summary.noPlans)
    : { label: PROCESS_RUNNING_LABEL, color: "processing", tone };
  const estimate = estimateRemaining(summary);

  // Documento anterior à instrumentação por filial (spec 027, RN04): sem `plans_count` nem
  // `branches`, os números não existem — e ausência não é zero (RN05).
  const notInstrumented =
    summary.total > 0 && !summary.reportsPlans && !ramos.reported;

  const visibleRows = useMemo(
    () => filterRows(rows, rowFilter, search, withoutFamily, branchLabels),
    [rows, rowFilter, search, withoutFamily, branchLabels]
  );

  const counts = {
    all: summary.total,
    fail: summary.failures,
    empty: summary.emptyBranches,
    nofamily: summary.withoutFamilyBranches,
  };

  async function redispatch() {
    setRedispatching(true);
    try {
      const response = await axiosInstance.get(`${apiUrl}/v1/consumers/plans`);
      const processId: string | undefined = response.data?.process_id;
      toast.open?.({
        message: "Recarga iniciada",
        description: processId
          ? `Processo ${processId} em andamento.`
          : "Acompanhe o novo processo na lista.",
        type: "success",
      });
      // O disparo cria outro `process_id`; a lista é a superfície que o localiza (e já traz o
      // aviso de "recarga em andamento"), então não há salto para um detalhe inexistente.
      list("v1/consumer-log");
    } catch (error: any) {
      const body = error?.response?.data;
      toast.open?.({
        message: "Erro",
        description: body?.stage
          ? `Falha no disparo — etapa: ${body.stage}. ${body?.message ?? ""}`.trim()
          : "Não foi possível iniciar a recarga. Tente novamente.",
        type: "error",
      });
    } finally {
      setRedispatching(false);
    }
  }

  function confirmRedispatch() {
    Modal.confirm({
      title: "Reprocessar todas as operadoras",
      content:
        "A recarga busca os planos no SIGO e substitui a prateleira de cada filial, uma " +
        "transação por filial. Filial sem planos válidos no SIGO fica com a prateleira " +
        "zerada até a próxima recarga com sucesso. Esta carga é pesada e pode levar " +
        "alguns minutos.",
      okText: "Reprocessar",
      cancelText: "Cancelar",
      onOk: redispatch,
    });
  }

  return (
    <Show isLoading={isLoading} title="Recarga de Planos">
      <Card className="plan-reload-panel" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Space size="middle" align="center">
              <Text className="plan-reload-mono" copyable={{ text: record?.process_id }}>
                {record?.process_id ?? "-"}
              </Text>
              <span className={`plan-reload-pill plan-reload-pill--${status.tone}`}>
                <span className="plan-reload-pill__dot" />
                {status.label}
              </span>
            </Space>
          </Col>
          <Col>
            <Space align="center">
              {dataUpdatedAt ? (
                <Text type="secondary">
                  {`Atualizado ${formatElapsed((Date.now() - dataUpdatedAt) / 1000)}`}
                </Text>
              ) : null}
              <Button icon={<Icons.ReloadOutlined />} onClick={() => refetch()}>
                Atualizar
              </Button>
              {/* O `consumer_log` não guarda a operadora do disparo — só `is_full_process`.
                  Repetir "a mesma abrangência" só é honesto no processo total; no processo por
                  operadora o caminho é voltar ao disparo e escolher a operadora. */}
              {isAdmin && record?.is_full_process && (
                <Button
                  icon={<Icons.RedoOutlined />}
                  loading={redispatching}
                  onClick={confirmRedispatch}
                >
                  Reprocessar Novamente
                </Button>
              )}
              {isAdmin && record && !record.is_full_process && (
                <Button
                  icon={<Icons.RedoOutlined />}
                  onClick={() => list("v1/consumer-log")}
                >
                  Disparar Nova Recarga
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Descriptions
          size="small"
          bordered
          column={{ xs: 1, sm: 2, lg: 3 }}
          style={{ marginTop: 20 }}
        >
          <Descriptions.Item label="Origem">
            <Tag>{originLabel(record?.command_origin)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Abrangência">
            {record?.is_full_process ? "Todas as operadoras" : "Por operadora"}
          </Descriptions.Item>
          <Descriptions.Item label="Consumidor">
            {record?.consumer ?? "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Início">
            {dateFormater(record?.start_time) ?? "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Fim">
            {summary.done ? dateFormater(record?.end_time) ?? "-" : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Duração">
            {formatDuration(
              record?.start_time,
              summary.done ? record?.end_time : undefined
            )}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 24 }}>
          <Progress
            percent={summary.progress}
            strokeColor={TONE_COLOR[tone]}
            trailColor="var(--ant-panel-border)"
            strokeWidth={10}
            showInfo={false}
          />
          <Row justify="space-between" gutter={[16, 4]} style={{ marginTop: 12 }}>
            <Col>
              <Text type="secondary">
                {`${summary.finalized} de ${summary.total} filiais concluídas`}
                {!summary.done && summary.total > summary.finalized
                  ? ` · ${summary.total - summary.finalized} em andamento`
                  : ""}
              </Text>
            </Col>
            {estimate !== null && (
              <Col>
                <Text type="secondary">
                  {`conclusão estimada em ${formatEstimate(estimate)}`}
                </Text>
              </Col>
            )}
          </Row>
        </div>
      </Card>

      <ProcessAlerts
        summary={summary}
        withoutFamilyPlans={withoutFamily.totalPlans}
        notInstrumented={notInstrumented}
        onFilter={setRowFilter}
      />

      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        <StatCard
          brand
          icon={<Icons.BankOutlined />}
          value={`${summary.finalized}/${summary.total}`}
          label="Filiais concluídas"
          caption="Filiais finalizadas sobre o total do processo"
        />
        <StatCard
          icon={<Icons.FileOutlined />}
          value={metricNumber(summary.totalPlans, summary.reportsPlans)}
          label="Planos aplicados"
          caption={
            summary.reportsPlans
              ? "Soma dos planos trocados em cada filial"
              : "Não registrado neste processo"
          }
          muted={!summary.reportsPlans}
          notReported={!summary.reportsPlans}
        />
        <StatCard
          icon={<Icons.CloseCircleOutlined />}
          value={metricNumber(summary.failures)}
          label="Falhas"
          caption="Filiais com desfecho Falha ou Parcial"
          tone={summary.failures ? "error" : undefined}
          muted={!summary.failures}
        />
        <StatCard
          icon={<Icons.InboxOutlined />}
          value={metricNumber(summary.emptyBranches)}
          label="Sem planos"
          caption="Filiais que responderam sem nenhum plano"
          tone={summary.emptyBranches ? "warning" : undefined}
          muted={!summary.emptyBranches}
        />
        <StatCard
          icon={<Icons.TagsOutlined />}
          value={metricNumber(withoutFamily.totalPlans)}
          label="Planos sem família"
          caption="Planos-base descartados por falta de palavra-chave"
          tone={withoutFamily.totalPlans ? "warning" : undefined}
          muted={!withoutFamily.totalPlans}
        />
        <StatCard
          icon={<Icons.ClockCircleOutlined />}
          value={summary.avgTime ? formatSeconds(summary.avgTime) : "-"}
          label="Tempo médio"
          caption={
            summary.maxTime
              ? `Média por filial · máximo ${formatSeconds(summary.maxTime)}`
              : "Média de execução por filial"
          }
          muted={!summary.avgTime}
        />
      </Row>

      {ramos.reported && <RamoPanel ramos={ramos} />}

      <Card
        className="plan-reload-card"
        style={{ marginTop: 16, borderRadius: 20 }}
        title="Filiais"
        extra={
          <Space wrap>
            <Radio.Group
              size="small"
              value={rowFilter}
              onChange={(e) => setRowFilter(e.target.value as RowFilter)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="all">{`Todas (${counts.all})`}</Radio.Button>
              <Radio.Button value="fail">{`Falhas (${counts.fail})`}</Radio.Button>
              <Radio.Button value="empty">{`Sem planos (${counts.empty})`}</Radio.Button>
              <Radio.Button value="nofamily">
                {`Sem família (${counts.nofamily})`}
              </Radio.Button>
            </Radio.Group>
            <Input.Search
              size="small"
              allowClear
              placeholder="Filial"
              style={{ width: 160 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Space>
        }
      >
        <Table<BranchRow>
          dataSource={visibleRows}
          rowKey="bo_id"
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          size="small"
          scroll={{ x: 900 }}
          rowClassName={(row) => (isFailure(row) ? "plan-reload-row-failure" : "")}
          locale={{
            emptyText:
              rowFilter === "all"
                ? "Este processo ainda não registrou nenhuma filial."
                : "Nenhuma filial neste recorte.",
          }}
          expandable={{
            rowExpandable: (row) =>
              (withoutFamily.byBranch[row.bo_id] ?? []).length > 0,
            expandedRowRender: (row) => (
              <BranchWithoutFamily
                plans={withoutFamily.byBranch[row.bo_id] ?? []}
              />
            ),
          }}
        >
          <Table.Column<BranchRow>
            dataIndex="bo_id"
            title="Filial"
            sorter={(a, b) => a.bo_id.localeCompare(b.bo_id)}
            render={(_, row) => (
              <BranchCell row={row} labels={branchLabels} />
            )}
          />
          <Table.Column<BranchRow>
            title="Desfecho"
            dataIndex="outcome"
            render={(_, row) => <OutcomeTag row={row} />}
          />
          <Table.Column<BranchRow>
            title="Planos"
            dataIndex="plans_count"
            align="right"
            render={(value) => metricNumber(value)}
            sorter={(a, b) => (a.plans_count ?? 0) - (b.plans_count ?? 0)}
          />
          <Table.ColumnGroup title="Ramos">
            {ramos.ramos.map((ramo) => (
              <Table.Column<BranchRow>
                key={ramo.key}
                title={
                  <Tooltip title={ramo.hint}>
                    <span className="plan-reload-ramo__hint">
                      {RAMO_SHORT[ramo.key]}
                    </span>
                  </Tooltip>
                }
                dataIndex={["branches", ramo.key]}
                align="right"
                render={(_, row) => metricNumber(row.branches?.[ramo.key])}
              />
            ))}
          </Table.ColumnGroup>
          <Table.Column<BranchRow>
            title="Sem família"
            dataIndex="without_family"
            align="right"
            render={(_, row) => (
              <WithoutFamilyCell row={row} summary={withoutFamily} />
            )}
          />
          <Table.Column<BranchRow>
            title="Tempo"
            dataIndex="exec_time"
            align="right"
            render={(value) => (value ? formatSeconds(Number(value)) : "-")}
            sorter={(a, b) => (a.exec_time ?? 0) - (b.exec_time ?? 0)}
          />
          <Table.Column<BranchRow>
            title="Motivo"
            dataIndex="error"
            render={(value) => <ReasonCell reason={value} />}
          />
        </Table>
      </Card>

      {withoutFamily.totalPlans > 0 && (
        <WithoutFamilyPanel groups={withoutFamily.consolidated} />
      )}
    </Show>
  );
};

/** Cabeçalho curto das subcolunas de ramo — o nome completo fica no tooltip. */
const RAMO_SHORT: Record<string, string> = {
  incond_old: "Inc",
  standard_old: "Pad",
  campaign_new: "Camp",
  gross: "Bruto",
};

export const filterRows = (
  rows: BranchRow[],
  rowFilter: RowFilter,
  search: string,
  withoutFamily: WithoutFamilySummary,
  labels: BranchOfficeLabels = {}
): BranchRow[] => {
  let visible = rows;

  if (rowFilter === "fail") visible = visible.filter(isFailure);
  else if (rowFilter === "empty") visible = visible.filter(isEmpty);
  else if (rowFilter === "nofamily") {
    visible = visible.filter((row) => branchWithoutFamilyCount(row, withoutFamily) > 0);
  }

  const term = search.trim().toLowerCase();
  if (!term) return visible;

  // Busca pelo código, pela UF e pelo NOME DA ÁREA — o rótulo exibido colapsa as áreas numa
  // contagem, mas o operador procura pela cidade que conhece.
  return visible.filter((row) => {
    const haystack = branchOfficeSearchText(labels[row.bo_id]).toLowerCase();
    return row.bo_id.toLowerCase().includes(term) || haystack.includes(term);
  });
};

/**
 * Avisos do processo — cada sinal vira frase e ação (spec 036, D05). A precedência é a mesma da
 * cor da barra: falha → zero plano → sem planos → sem família.
 */
const ProcessAlerts: React.FC<{
  summary: ReturnType<typeof summarize>;
  withoutFamilyPlans: number;
  notInstrumented: boolean;
  onFilter: (filter: RowFilter) => void;
}> = ({ summary, withoutFamilyPlans, notInstrumented, onFilter }) => {
  const alerts: React.ReactNode[] = [];

  if (notInstrumented) {
    alerts.push(
      <Alert
        key="legacy"
        type="info"
        showIcon
        message="Processo anterior à instrumentação por filial"
        description="Volume, ramos e desfechos não foram registrados neste processo."
      />
    );
  }

  // Spec 062: planos antigos no ar é o único estado em que se vende dado não confirmado —
  // vem antes de tudo (REV06 do gpm_review).
  if (summary.staleBranches > 0) {
    alerts.push(
      <Alert
        key="stale"
        type="error"
        showIcon
        message={
          summary.staleBranches === 1
            ? "1 filial com planos antigos no ar."
            : `${summary.staleBranches} filiais com planos antigos no ar.`
        }
        description="Nem a troca nem a limpeza puderam ser gravadas. Essas filiais continuam vendendo planos não confirmados por esta recarga — reprocesse assim que possível."
        action={
          <Button size="small" onClick={() => onFilter("fail")}>
            Ver só falhas
          </Button>
        }
      />
    );
  }

  if (summary.failures > 0) {
    alerts.push(
      <Alert
        key="fail"
        type="error"
        showIcon
        message={
          summary.failures === 1
            ? "1 filial falhou na recarga."
            : `${summary.failures} filiais falharam na recarga.`
        }
        description="Filial sem planos válidos nesta recarga fica com a prateleira zerada até a próxima recarga com sucesso. Veja o estado da prateleira na tabela."
        action={
          <Button size="small" onClick={() => onFilter("fail")}>
            Ver só falhas
          </Button>
        }
      />
    );
  }

  // Zero plano no processo inteiro é o sinal mais inequívoco disponível (spec 033, RN12).
  if (summary.noPlans) {
    alerts.push(
      <Alert
        key="noplans"
        type="warning"
        showIcon
        message="Recarga concluída sem nenhum plano coletado"
        description="Nenhuma filial recebeu planos nesta recarga: as prateleiras foram zeradas e ficam sem planos à venda até a próxima recarga com sucesso."
        action={
          <Button size="small" onClick={() => onFilter("all")}>
            Ver filiais
          </Button>
        }
      />
    );
  }

  if (summary.emptyBranches > 0) {
    alerts.push(
      <Alert
        key="empty"
        type="warning"
        showIcon
        message={
          summary.emptyBranches === 1
            ? "1 filial concluiu sem planos disponíveis no SIGO."
            : `${summary.emptyBranches} filiais concluíram sem planos disponíveis no SIGO.`
        }
        description="Não é falha: o SIGO respondeu com sucesso e sem conteúdo. A prateleira dessas filiais foi zerada e fica sem planos à venda até a próxima recarga com sucesso."
        action={
          <Button size="small" onClick={() => onFilter("empty")}>
            Ver sem planos
          </Button>
        }
      />
    );
  }

  if (withoutFamilyPlans > 0) {
    alerts.push(
      <Alert
        key="nofamily"
        type="warning"
        showIcon
        message={
          withoutFamilyPlans === 1
            ? "1 plano-base descartado por falta de família."
            : `${withoutFamilyPlans} planos-base descartados por falta de família.`
        }
        description="O nome do plano não casa com nenhuma palavra-chave cadastrada, então ele não entra na prateleira e não é vendido."
        action={
          // Nova aba: a tela faz polling e navegar aqui abandonaria o acompanhamento (RN08/035).
          <Button
            size="small"
            href={PLANS_FAMILY_ROUTE}
            target="_blank"
            rel="noreferrer"
            icon={<Icons.ExportOutlined />}
          >
            Cadastrar palavra-chave
          </Button>
        }
      />
    );
  }

  if (!alerts.length) return null;

  return (
    <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 16 }}>
      {alerts}
    </Space>
  );
};

/** Card de número no padrão do `DashboardV2` — nenhum layout de métrica novo (componentes.md). */
const StatCard: React.FC<{
  value: React.ReactNode;
  label: string;
  caption: string;
  icon: React.ReactNode;
  brand?: boolean;
  tone?: "error" | "warning";
  muted?: boolean;
  notReported?: boolean;
}> = ({ value, label, caption, icon, brand, tone, muted, notReported }) => {
  const valueClass = [
    "plan-reload-stat__value",
    tone ? `plan-reload-stat__value--${tone}` : "",
    !tone && muted ? "plan-reload-stat__value--muted" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // `div` e não `Typography.Title`: seis números não são seis cabeçalhos de nível 1 (a11y), e o
  // tamanho vem da classe sem disputar especificidade com `h1.ant-typography`.
  const number = <div className={valueClass}>{value}</div>;

  return (
    <Col xs={24} sm={12} lg={8} xxl={4}>
      <Card
        className={`plan-reload-stat${brand ? " plan-reload-stat--brand" : ""}`}
        bodyStyle={{ padding: 28 }}
        bordered={false}
      >
        <div className="plan-reload-stat__icon">{icon}</div>
        {notReported ? (
          <Tooltip title="Processo anterior à instrumentação">{number}</Tooltip>
        ) : (
          number
        )}
        <Text className="plan-reload-stat__label">{label}</Text>
        <Text className="plan-reload-stat__caption">{caption}</Text>
      </Card>
    </Col>
  );
};

/**
 * Composição por ramo de preço (spec 008) — ordem de prioridade, não alfabética.
 *
 * O Plan.Producer classifica cada plano com a campanha nova vencendo o coalesce legado quando
 * há `cd_campaign_discount` + preço de 1ª parcela utilizável. Antes o card mostrava o ramo cru
 * do coalesce e, como o catálogo do SIGO manda `nuVlMensaLiquidaIncond` igual ao bruto (desconto
 * 0) nos planos de campanha, dava 100% incondicional / 0% campanha nova.
 *
 * "Bruto" continua sendo o alarme: plano com código de campanha mas sem preço de campanha cai
 * aqui, e não em "Campanha nova".
 */
const RamoPanel: React.FC<{ ramos: RamoSummary }> = ({ ramos }) => (
  <Card
    title="Composição por Ramo"
    style={{ marginTop: 16, borderRadius: 20 }}
    extra={<Text type="secondary">{`${metricNumber(ramos.total)} planos`}</Text>}
  >
    <Text type="secondary">
      Ramo do desconto de cada plano na ingestão. Campanha nova conta quando o SIGO manda código
      de desconto de campanha com preço de 1ª parcela; sem isso, vale o desconto legado
      (incondicional → padrão) ou Bruto. Um salto em Bruto indica plano sem desconto vigente —
      inclusive plano com código de campanha que veio sem preço de campanha.
    </Text>
    <div style={{ marginTop: 16 }}>
      {ramos.ramos.map((ramo, index) => (
        <Row key={ramo.key} gutter={16} align="middle" style={{ padding: "4px 0" }}>
          <Col xs={12} sm={8}>
            <Tooltip title={ramo.hint}>
              <span className="plan-reload-ramo__hint">{ramo.label}</span>
            </Tooltip>
          </Col>
          <Col xs={6} sm={3} style={{ textAlign: "right" }}>
            <Text strong>{metricNumber(ramo.count)}</Text>
          </Col>
          <Col xs={6} sm={3} style={{ textAlign: "right" }}>
            <Text type="secondary">
              {`${ramo.percent.toLocaleString("pt-BR", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}%`}
            </Text>
          </Col>
          <Col xs={0} sm={10}>
            <div className="plan-reload-ramo__track">
              <div
                className="plan-reload-ramo__fill"
                style={{
                  width: `${ramo.percent}%`,
                  // Opacidade decrescente pela prioridade: nenhum ramo é erro, então cor
                  // semântica está fora de questão.
                  opacity: 1 - index * 0.2,
                }}
              />
            </div>
          </Col>
        </Row>
      ))}
    </div>
  </Card>
);

const BranchCell: React.FC<{ row: BranchRow; labels: BranchOfficeLabels }> = ({
  row,
  labels,
}) => {
  const label = labels[row.bo_id];
  const text = branchOfficeLabelText(label);

  return (
    <div>
      <Text className="plan-reload-mono">{row.bo_id}</Text>
      {text ? (
        <Tooltip title={label?.areas.join(", ")}>
          <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
            {text}
          </Text>
        </Tooltip>
      ) : null}
    </div>
  );
};

const OutcomeTag: React.FC<{ row: BranchRow }> = ({ row }) => {
  const outcome = row.outcome ? OUTCOME[row.outcome] : undefined;
  // Spec 062 — o desfecho diz o que aconteceu; o estado da prateleira diz o que está à venda.
  const shelf = row.shelf_state ? SHELF_STATE[row.shelf_state] : undefined;

  if (outcome) {
    return (
      <Space size={4} wrap>
        <Tooltip title={outcome.tooltip}>
          <Tag color={outcome.color}>{outcome.label}</Tag>
        </Tooltip>
        {shelf ? (
          <Tooltip title={shelf.tooltip}>
            <Tag color={shelf.color}>{shelf.label}</Tag>
          </Tooltip>
        ) : null}
      </Space>
    );
  }

  // Sem `outcome`: ou a filial ainda está executando (dois produtores escrevem no mesmo
  // process_id — RN12 da 027), ou é documento antigo. Nos dois casos, o status cru é a verdade.
  return (
    <Tag color={row.status === "finalizado" ? "green" : "processing"}>
      {row.status ?? "-"}
    </Tag>
  );
};

/**
 * O motivo já chega sanitizado da escrita (RN03 da 027 / RN02 da 028): a tela **não** traduz,
 * complementa nem infere causa. Trunca para caber na linha e entrega o texto integral no popover.
 */
export const ReasonCell: React.FC<{ reason?: string }> = ({ reason }) => {
  if (!reason) return <Text type="secondary">-</Text>;

  return (
    <Space size={4} align="start">
      <span className="plan-reload-reason">{reason}</span>
      <Popover
        trigger={["hover", "focus", "click"]}
        title="Motivo da falha"
        content={<div className="plan-reload-reason-full">{reason}</div>}
      >
        <Button type="link" size="small">
          Ver motivo
        </Button>
      </Popover>
    </Space>
  );
};

/**
 * Exportado para teste: dentro do `render` de uma `Table.Column` o harness da suíte não o
 * alcança, e a fiação (contador da filial × detalhe agregado) é justamente o que pode quebrar.
 */
export const WithoutFamilyCell: React.FC<{
  row: BranchRow;
  summary: WithoutFamilySummary;
}> = ({ row, summary }) => {
  const { value, tooltip } = withoutFamilyDisplay(
    row.without_family ?? 0,
    (summary.byBranch[row.bo_id] ?? []).length
  );

  if (value === null) return <Text type="secondary">—</Text>;
  if (!tooltip) return <Text className="plan-reload-warn">{value}</Text>;

  return (
    <Tooltip title={tooltip}>
      <Text className="plan-reload-warn plan-reload-warn--help">{value}</Text>
    </Tooltip>
  );
};

/** Recorte por filial (linha expandida da tabela). */
const BranchWithoutFamily: React.FC<{ plans: WithoutFamilyPlan[] }> = ({
  plans,
}) => (
  <div style={{ padding: "2px 0 2px 8px" }}>
    <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
      {plans.length === 1
        ? "1 plano descartado por falta de família"
        : `${plans.length} planos descartados por falta de família`}
    </Text>
    {plans.map((plan) => (
      <Space key={`${plan.base_plan}|${plan.nm_plan}`} size="middle" align="baseline">
        <Text type="secondary" className="plan-reload-mono" style={{ fontSize: 12 }}>
          {plan.base_plan}
        </Text>
        <Text copyable={{ text: plan.nm_plan }}>{plan.nm_plan}</Text>
      </Space>
    ))}
  </div>
);

/**
 * Lista de filiais afetadas, truncada em 5 + `+N` (a lista inteira fica no tooltip). Exportado
 * porque vive dentro do `render` de uma `Table.Column` — inalcançável pelo harness da suíte.
 */
export const BranchList: React.FC<{ branches: string[] }> = ({ branches }) => {
  const shown = branches.slice(0, 5);
  const rest = branches.length - shown.length;

  return (
    <Text type="secondary" className="plan-reload-mono" style={{ fontSize: 12 }}>
      {shown.join(", ")}
      {rest > 0 && (
        <Tooltip title={branches.join(", ")}>
          <span style={{ cursor: "help" }}>{` +${rest}`}</span>
        </Tooltip>
      )}
    </Text>
  );
};

/**
 * Visão consolidada do processo: o mesmo plano tipicamente repete em dezenas de filiais, e o que
 * o operador precisa é da lista de PLANOS a cadastrar — não de repetir a filial 30 vezes.
 */
const WithoutFamilyPanel: React.FC<{ groups: WithoutFamilyGroup[] }> = ({
  groups,
}) => {
  const branchCount = new Set(groups.flatMap((group) => group.branches)).size;

  return (
    <Card
      title="Planos sem Família"
      style={{ marginTop: 16, borderRadius: 20 }}
      extra={
        <a href={PLANS_FAMILY_ROUTE} target="_blank" rel="noreferrer">
          {"Família de Planos "}
          <Icons.ExportOutlined />
        </a>
      }
    >
      <Text type="secondary">
        {groups.length === 1 ? "1 plano" : `${groups.length} planos`}
        {" · "}
        {branchCount === 1 ? "1 filial" : `${branchCount} filiais`}
        {" — descartados da prateleira porque o nome não casa com nenhuma palavra-chave de " +
          "família. Cadastre a palavra-chave e redispare a recarga."}
      </Text>

      <Table<WithoutFamilyGroup>
        dataSource={groups}
        rowKey="key"
        size="small"
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        style={{ marginTop: 16 }}
      >
        <Table.Column<WithoutFamilyGroup>
          title="Plano Base"
          dataIndex="base_plan"
          render={(value) => (
            <Text type="secondary" className="plan-reload-mono" style={{ fontSize: 12 }}>
              {value}
            </Text>
          )}
        />
        <Table.Column<WithoutFamilyGroup>
          title="Nome do Plano"
          dataIndex="nm_plan"
          render={(value) => <Text copyable={{ text: value }}>{value}</Text>}
        />
        <Table.Column<WithoutFamilyGroup>
          title="Filiais"
          dataIndex="branches"
          render={(_, group) => <BranchList branches={group.branches} />}
        />
      </Table>
    </Card>
  );
};
